/**
 * useChildCurrentLogic.js
 * React hook for Child Current Vaccination scenario
 *
 * Phase 3: Encapsulates ChildCurrentRules inside a stable hook interface
 * that matches the DoseConfig contract established by Phases 1 and 2.
 *
 * WHY A SEPARATE HOOK FROM THE RULES FILE:
 * ChildCurrentRules.js is pure JS – it can be unit-tested without React.
 * This hook is the React adapter layer. It handles:
 *   - useMemo to prevent recalculating configs on every render
 *   - A consistent return shape so VaccinationTabs can consume child
 *     and PW scenarios interchangeably
 *   - An "enhanced" variant with convenience helpers for complex UIs
 *
 * IMPORTANT – the decryptFunction dependency in useMemo:
 * If the parent component creates the decrypt function inline (no useCallback),
 * it will be a new reference on every render, busting the memo. Callers should
 * wrap the function in useCallback or pass a stable reference (e.g., a module-
 * level import from the helpers file).
 */

import { useMemo, useCallback } from 'react';
import {
  getChildCurrentDoseStatus,
  calculateChildAgeInDays
} from './ChildCurrentRules';

/**
 * Base hook for Child Current vaccination logic.
 *
 * @param {Object}   vaccine           - Vaccine object { id, name, availableDoses, material }
 * @param {string}   childDob          - Child's date of birth string
 * @param {Array}    administeredDoses - Vaccination history array from the profile API
 * @param {Function} decryptFunction   - Stable decrypt function (wrap in useCallback at call site)
 * @returns {{
 *   getDoseConfig  : (doseIndex: number) => DoseConfig,
 *   scenario       : string,
 *   isReady        : boolean,
 *   childAgeInDays : number
 * }}
 */
export const useChildCurrentLogic = (
  vaccine,
  childDob,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  // Pre-calculate age so it is consistent across all dose configs in one render cycle.
  // This also exposes the value to callers who want to display the age.
  const childAgeInDays = useMemo(
    () => calculateChildAgeInDays(childDob),
    [childDob]
  );

  /**
   * getDoseConfig is the primary output of this hook.
   *
   * It is memoized on all four inputs so that if the parent re-renders
   * without changing any of these values, the function reference remains
   * stable and downstream components (wrapped in React.memo) will not
   * unnecessarily re-render.
   */
  const getDoseConfig = useMemo(() => {
    return (doseIndex) => {
      try {
        const rawStatus = getChildCurrentDoseStatus(
          vaccine,
          doseIndex,
          childDob,
          administeredDoses,
          decryptFunction,
          identityContext
        );

        // Normalise into the DoseConfig contract.
        // Always provide every key so consumers don't need optional-chaining
        // for properties they never set.
        return {
          // UI interaction flags
          enabled: rawStatus.enabled ?? false,
          showRadio: rawStatus.showRadio ?? false,

          // Status string – drives conditional rendering in VaccinationTabs
          status: rawStatus.status ?? 'Unknown',

          // Date shown when status === 'administered'
          date: rawStatus.date ?? null,

          // Human-readable explanation for non-eligible states
          reason: rawStatus.reason ?? null,

          // Metadata – useful for debugging and for VaccinationTabs to
          // know which scenario it is dealing with
          scenario: 'child-current',
          vaccineId: vaccine?.id ?? null,
          vaccineName: vaccine?.name ?? null,
          doseIndex,

          // Child-specific extra – age at time of render
          ageInDays: rawStatus.ageInDays ?? childAgeInDays
        };
      } catch (error) {
        // Return a safe fallback rather than letting an error propagate
        // to the Formik render. The UI will show a disabled state.
        console.error(
          `useChildCurrentLogic.getDoseConfig error for vaccine "${vaccine?.name}" dose ${doseIndex}:`,
          error
        );

        return {
          enabled: false,
          showRadio: false,
          status: 'Error',
          date: null,
          reason: 'Could not determine eligibility – please refresh',
          scenario: 'child-current',
          vaccineId: vaccine?.id ?? null,
          vaccineName: vaccine?.name ?? null,
          doseIndex,
          ageInDays: childAgeInDays
        };
      }
    };
  }, [vaccine, childDob, administeredDoses, decryptFunction, identityContext]);

  return {
    getDoseConfig,
    scenario: 'child-current',
    // isReady lets callers defer rendering until all data is available.
    // This mirrors the pattern from usePWCurrentLogic and usePWPreviousLogic.
    isReady: !!(vaccine && childDob && decryptFunction)
  };
};

// ============================================================
// Enhanced hook – includes batch config calculation and helpers
// ============================================================

/**
 * Enhanced hook for Child Current vaccination.
 *
 * Extends the base hook with:
 *   - allDoseConfigs: computed array for all doses in a single memoized pass
 *   - Summary booleans (hasEligibleDoses, hasAdministeredDoses)
 *   - Convenience callbacks (isDoseEnabled, getDoseStatus)
 *
 * Use this variant when the parent component needs to display summary state
 * (e.g., "X vaccines are eligible") without calling getDoseConfig in a loop.
 *
 * @param {Object}   vaccine           - Vaccine object
 * @param {string}   childDob          - Child's date of birth
 * @param {Array}    administeredDoses - Vaccination history
 * @param {Function} decryptFunction   - Stable decrypt function
 * @param {Object}   [options]         - Optional config
 * @param {boolean}  [options.debug]   - Include debugInfo in return value
 * @returns {Object} - Enhanced hook result
 */
export const useChildCurrentLogicEnhanced = (
  vaccine,
  childDob,
  administeredDoses,
  decryptFunction,
  identityContext = null,
  options = {}
) => {
  const baseLogic = useChildCurrentLogic(
    vaccine,
    childDob,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  // Compute all dose configs in a single memoized pass.
  // This is more efficient than individual getDoseConfig calls in a map()
  // because it avoids creating a new function closure per dose per render.
  const allDoseConfigs = useMemo(() => {
    if (!baseLogic.isReady || !vaccine?.availableDoses?.length) {
      return [];
    }

    return vaccine.availableDoses.map((_, index) => baseLogic.getDoseConfig(index));
  // We depend on baseLogic.getDoseConfig via reference stability from the base hook.
  // Adding vaccine.availableDoses ensures we recompute when the dose list changes.
  }, [baseLogic, vaccine?.availableDoses]);

  // Stable callbacks that consumers can pass as props without causing child re-renders
  const isDoseEnabled = useCallback(
    (doseIndex) => allDoseConfigs[doseIndex]?.enabled ?? false,
    [allDoseConfigs]
  );

  const getDoseStatus = useCallback(
    (doseIndex) => allDoseConfigs[doseIndex]?.status ?? 'Unknown',
    [allDoseConfigs]
  );

  return {
    // Spread base logic so callers can use getDoseConfig directly if needed
    ...baseLogic,

    // Batch-computed configs
    allDoseConfigs,

    // Convenience helpers
    isDoseEnabled,
    getDoseStatus,

    // Summary flags – useful for driving a "nothing eligible" empty-state UI
    hasEligibleDoses: allDoseConfigs.some((c) => c.status === 'Eligible'),
    hasAdministeredDoses: allDoseConfigs.some((c) => c.status === 'administered'),
    hasUpcomingDoses: allDoseConfigs.some((c) => c.status === 'Upcoming'),
    hasExpiredDoses: allDoseConfigs.some((c) => c.status === 'AgeExpired'),

    // Optional debug info – only populated when options.debug is true to
    // avoid attaching large objects to the hook return in production
    debugInfo: options.debug
      ? {
          vaccine: vaccine?.name,
          childDob,
          childAgeInDays: baseLogic.childAgeInDays,
          administeredCount: administeredDoses?.length ?? 0,
          doseConfigs: allDoseConfigs
        }
      : null
  };
};
